'use client'

import { memo, useEffect, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import {
  useNinxl8BitThemeMode,
  type Ninxl8BitTheme,
} from '@/app/ninxl/_components/use-ninxl-8bit-theme'

type PixelProps = {
  x: number
  y: number
  w?: number
  h?: number
  fill: string
}

/** baby: one logical pixel = 4 SVG units */
const P = ({ x, y, w = 1, h = 1, fill }: PixelProps) => (
  <rect x={x * 4} y={y * 4} width={w * 4} height={h * 4} fill={fill} />
)

const STARS: Array<{ x: number; y: number; delay: string }> = [
  { x: 6, y: 2, delay: '0s' },
  { x: 18, y: 5, delay: '0.6s' },
  { x: 32, y: 3, delay: '1.1s' },
  { x: 48, y: 6, delay: '0.3s' },
  { x: 64, y: 2, delay: '1.5s' },
  { x: 118, y: 4, delay: '0.9s' },
  { x: 132, y: 7, delay: '0.2s' },
  { x: 148, y: 3, delay: '1.3s' },
]

type ScenePalette = {
  sky: string
  wall: string
  floor: string
  window: string
  buildingA: string
  buildingB: string
  buildingC: string
  buildingD: string
  windowLight: string
  desk: string
  deskEdge: string
  tower: string
  towerInner: string
  localLabel: string
  cable: string
  monitorBezel: string
  stand: string
  standBase: string
  keyboard: string
  key: string
  mic: string
  micBase: string
  micTip: string
  playerHead: string
  playerBody: string
  playerLeg: string
  cloud: string
  wave: string
  celestial: string
  celestialCutout: string
  accent: string
}

const SCENE_PALETTE: Record<Ninxl8BitTheme, ScenePalette> = {
  dark: {
    sky: '#050505',
    wall: '#0e0e0e',
    floor: '#161616',
    window: '#080808',
    buildingA: '#1a1a1a',
    buildingB: '#222222',
    buildingC: '#1a1a1a',
    buildingD: '#2a2a2a',
    windowLight: '#ffffff',
    desk: '#5a5a5a',
    deskEdge: '#3a3a3a',
    tower: '#1a1a1a',
    towerInner: '#111111',
    localLabel: '#ffffff',
    cable: '#555555',
    monitorBezel: '#2a2a2a',
    stand: '#444444',
    standBase: '#333333',
    keyboard: '#1a1a1a',
    key: '#444444',
    mic: '#888888',
    micBase: '#666666',
    micTip: '#ffffff',
    playerHead: '#cccccc',
    playerBody: '#777777',
    playerLeg: '#555555',
    cloud: '#222222',
    wave: '#444444',
    celestial: '#dddddd',
    celestialCutout: '#050505',
    accent: '#2dd4bf',
  },
  light: {
    sky: '#c8e4f4',
    wall: '#f0ebe3',
    floor: '#d8d0c4',
    window: '#e8f4fc',
    buildingA: '#c5d0d8',
    buildingB: '#b0bcc8',
    buildingC: '#a8b4c0',
    buildingD: '#98a8b8',
    windowLight: '#21728d',
    desk: '#a08060',
    deskEdge: '#806040',
    tower: '#e8e4dc',
    towerInner: '#d8d0c4',
    localLabel: '#0a1018',
    cable: '#88a1ac',
    monitorBezel: '#444444',
    stand: '#666666',
    standBase: '#888888',
    keyboard: '#d8d0c4',
    key: '#a09890',
    mic: '#5f7380',
    micBase: '#88a1ac',
    micTip: '#21728d',
    playerHead: '#5f7380',
    playerBody: '#88a1ac',
    playerLeg: '#5f7380',
    cloud: '#d0e4f0',
    wave: '#88a1ac',
    celestial: '#f5d76e',
    celestialCutout: '#c8e4f4',
    accent: '#21728d',
  },
}

const DESK_APPS = [
  {
    id: 'slack',
    title: 'Slack · #product',
    typed: 'Ship notes: voice typed here.',
    spoken: '“ship the notes…”',
  },
  {
    id: 'docs',
    title: 'Docs · Recap',
    typed: 'Meeting recap drafted from voice.',
    spoken: '“capture the recap…”',
  },
  {
    id: 'mail',
    title: 'Mail · Draft',
    typed: 'Thanks: sending the update now.',
    spoken: '“reply to the client…”',
  },
  {
    id: 'notes',
    title: 'Notes · Scratch',
    typed: 'Hold Shift · speak · release.',
    spoken: '“park this idea…”',
  },
] as const

const APP_CYCLE_MS = 3800

/**
 * baby: ninxl-style desk room: monitor is a prop in the scene, not the whole frame.
 * Person talks; apps switch on the small screen; text types; stays local.
 */
export const NinkProductivityScene = memo(function NinkProductivityScene({
  className,
  theme: themeProp,
}: {
  className?: string
  theme?: Ninxl8BitTheme
}) {
  const contextTheme = useNinxl8BitThemeMode()
  const theme = themeProp ?? contextTheme
  const p = SCENE_PALETTE[theme]
  const isDark = theme === 'dark'
  const reduceMotion = useReducedMotion()
  const [appIndex, setAppIndex] = useState(0)
  const app = DESK_APPS[appIndex] ?? DESK_APPS[0]

  useEffect(() => {
    if (reduceMotion) return
    const id = window.setInterval(() => {
      setAppIndex((prev) => (prev + 1) % DESK_APPS.length)
    }, APP_CYCLE_MS)
    return () => window.clearInterval(id)
  }, [reduceMotion])

  return (
    <svg
      viewBox="0 0 640 360"
      className={cn('ninxl-pixel-scene h-full w-full', className)}
      aria-hidden
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width="640" height="360" fill={p.sky} />

      {isDark
        ? STARS.map((star) => (
            <rect
              key={`${star.x}-${star.y}`}
              x={star.x * 4}
              y={star.y * 4}
              width={4}
              height={4}
              fill="#fff"
              className="ninxl-8bit-star"
              style={{ animationDelay: star.delay }}
            />
          ))
        : null}

      <g className="ninxl-8bit-moon-glow">
        <P x={152} y={4} w={5} h={5} fill={p.celestial} />
        {isDark ? <P x={153} y={5} w={3} h={3} fill={p.celestialCutout} /> : null}
      </g>

      <P x={0} y={38} w={160} h={52} fill={p.wall} />
      <P x={0} y={82} w={160} h={8} fill={p.floor} />

      {/* Window + city */}
      <P x={118} y={14} w={32} h={26} fill={p.window} />
      <P x={122} y={30} w={6} h={14} fill={p.buildingA} />
      <P x={130} y={26} w={5} h={18} fill={p.buildingB} />
      <P x={137} y={22} w={7} h={22} fill={p.buildingC} />
      <P x={146} y={28} w={5} h={16} fill={p.buildingD} />
      <P x={132} y={26} w={1} h={1} fill={p.windowLight} />
      <P x={140} y={30} w={1} h={1} fill={p.windowLight} />

      {/* Desk */}
      <P x={14} y={56} w={90} h={5} fill={p.desk} />
      <P x={12} y={61} w={94} h={2} fill={p.deskEdge} />

      {/* Local tower PC */}
      <g>
        <P x={16} y={38} w={12} h={20} fill={p.tower} />
        <P x={17} y={39} w={10} h={18} fill={p.towerInner} />
        <text
          x={72}
          y={168}
          fill={p.localLabel}
          fontSize="6"
          fontFamily="monospace"
          fontWeight="bold"
        >
          LOCAL
        </text>
        <rect
          x={76}
          y={160}
          width={8}
          height={8}
          fill={p.accent}
          className="ninxl-8bit-blink"
        />
        <P x={19} y={44} w={5} h={5} fill={p.accent} />
        <path
          d="M 112 200 L 160 200 L 200 188"
          fill="none"
          stroke={p.cable}
          strokeWidth="2"
          strokeDasharray="6 4"
          className="ninxl-8bit-data-cable"
        />
      </g>

      {/* Monitor: sits in the room (not full-bleed) */}
      <g className="ninxl-8bit-monitor-glow">
        <P x={38} y={18} w={58} h={36} fill={p.monitorBezel} />
        <P x={40} y={20} w={54} h={32} fill="#000" />

        {/* Menu / tray */}
        <rect x={164} y={84} width={6} height={6} fill={p.accent} className="ninxl-8bit-blink" />
        <text x={174} y={90} fill="#888" fontSize="5" fontFamily="monospace">
          NINK · Dictate
        </text>
        <text x={300} y={90} fill={p.accent} fontSize="4" fontFamily="monospace">
          ON-DEVICE
        </text>

        {/* App switcher chips */}
        {DESK_APPS.map((item, index) => {
          const active = index === appIndex
          return (
            <g key={item.id}>
              <rect
                x={168 + index * 34}
                y={98}
                width={30}
                height={8}
                fill={active ? p.accent : '#1a1a1a'}
                stroke="#444"
                strokeWidth={0.5}
              />
              <text
                x={172 + index * 34}
                y={104}
                fill={active ? '#000' : '#888'}
                fontSize="3.5"
                fontFamily="monospace"
                fontWeight={active ? 'bold' : 'normal'}
              >
                {item.id.toUpperCase()}
              </text>
            </g>
          )
        })}

        {/* Active app window */}
        <P x={42} y={28} w={50} h={22} fill="#0a0a0a" />
        <rect x={168} y={116} width={200} height={10} fill="#151515" />
        <text x={172} y={124} fill="#aaa" fontSize="4" fontFamily="monospace">
          {app.title}
        </text>

        {/* Typed line from voice */}
        <text x={172} y={142} fill="#ddd" fontSize="4.5" fontFamily="monospace">
          {app.typed}
        </text>
        <rect
          x={172 + Math.min(app.typed.length * 2.6, 180)}
          y={136}
          width={3}
          height={8}
          fill={p.accent}
          className="ninxl-8bit-blink"
        />

        <text x={172} y={158} fill="#666" fontSize="3.5" fontFamily="monospace">
          {app.spoken}
        </text>
        <text x={172} y={172} fill={p.accent} fontSize="3.5" fontFamily="monospace">
          listening → typing · keys idle
        </text>
      </g>

      <P x={62} y={54} w={10} h={4} fill={p.stand} />
      <P x={58} y={58} w={18} h={2} fill={p.standBase} />

      {/* Keyboard idle */}
      <P x={52} y={60} w={30} h={3} fill={p.keyboard} />
      {Array.from({ length: 10 }).map((_, i) => (
        <P key={i} x={53 + i * 3} y={61} w={2} h={1} fill={p.key} />
      ))}

      {/* Mic pulsing while speaking */}
      <g className="ninxl-8bit-mic-pulse">
        <P x={86} y={58} w={2} h={4} fill={p.mic} />
        <P x={85} y={62} w={4} h={1} fill={p.micBase} />
        <P x={86} y={57} w={2} h={1} fill={p.micTip} />
      </g>

      {/* Person at desk: talking */}
      <g className="ninxl-8bit-player-idle">
        <P x={98} y={50} w={3} h={3} fill={p.playerHead} />
        <P x={97} y={53} w={5} h={4} fill={p.playerBody} />
        <P x={96} y={57} w={2} h={3} fill={p.playerLeg} />
        <P x={100} y={57} w={2} h={3} fill={p.playerLeg} />
        {/* Speech waves */}
        <rect
          x={416}
          y={196}
          width={3}
          height={8}
          fill={p.accent}
          className="ninxl-8bit-blink"
          opacity={0.9}
        />
        <rect x={422} y={194} width={3} height={12} fill={p.accent} opacity={0.55} />
        <rect
          x={428}
          y={198}
          width={3}
          height={6}
          fill={p.accent}
          className="ninxl-8bit-blink"
          opacity={0.7}
        />
      </g>

      <text x={408} y={220} fill={p.accent} fontSize="4" fontFamily="monospace">
        SPEAKING
      </text>

      {/* On-device shield hint */}
      <text
        x={168}
        y={300}
        fill={isDark ? '#ffffff' : '#21728d'}
        fontSize="6"
        fontFamily="monospace"
        opacity={isDark ? 0.35 : 0.45}
      >
        EVERYTHING STAYS LOCAL
      </text>

      <g className="ninxl-8bit-wave-line" opacity={0.25}>
        {Array.from({ length: 24 }).map((_, i) => (
          <rect key={i} x={i * 28} y={332} width={12} height={2} fill={p.wave} />
        ))}
      </g>
    </svg>
  )
})
